/**
 * Sentry frontend initialization — no-op unless REACT_APP_SENTRY_DSN
 * is available at build time (or global SENTRY_DSN provided by SSR).
 *
 * Usage:
 *   import { initSentry } from './utils/common/sentry';
 *   initSentry();  // call once in src/index.js after render
 *
 * To enable:
 *   1. Add `REACT_APP_SENTRY_DSN=<dsn>` to build env (Railway → Variables).
 *   2. Webpack must be configured to inline it via DefinePlugin or similar.
 *   Currently reads from window.__SENTRY_DSN__ if injected by SSR.
 */

let sentryClient = null;

function getDsn() {
    if (typeof window !== 'undefined' && window.__SENTRY_DSN__) {
        return window.__SENTRY_DSN__;
    }
    return null;
}

export function initSentry() {
    if (sentryClient) return sentryClient;
    const dsn = getDsn();
    if (!dsn) return null;

    return import('@sentry/react').then((Sentry) => {
        try {
            Sentry.init({
                dsn,
                environment: window.location.hostname.includes('worldauto.ge') ? 'production' : 'development',
                tracesSampleRate: 0.05,
                replaysSessionSampleRate: 0,
                replaysOnErrorSampleRate: 0.1,
                beforeSend(event) {
                    if (event.exception?.values?.[0]?.value?.match(/Non-Error promise rejection captured|ResizeObserver loop|Script error/)) {
                        return null;
                    }
                    return event;
                },
                ignoreErrors: [
                    'ResizeObserver loop',
                    'Non-Error promise rejection captured',
                    /n\.settled/i,
                    /reading\s+'settled'/i,
                ],
            });
            sentryClient = Sentry;
            return Sentry;
        } catch (err) {
            console.warn('[sentry] init failed:', err);
            return null;
        }
    }).catch(() => null);
}

export function captureError(err, context = {}) {
    if (sentryClient) {
        sentryClient.captureException(err, { extra: context });
    }
}

export function captureMessage(msg, level = 'info', context = {}) {
    if (sentryClient) {
        sentryClient.captureMessage(msg, { level, extra: context });
    }
}
